import { onMounted, onUnmounted } from 'vue'
import { useActivityStore } from '@/stores/activity'
import { createLogger } from '@/shared/logger'

const logger = createLogger('ActivityComposable')

// Configuration based on environment variable for activity logs control
const enableActivityLogs = import.meta.env.VITE_ENABLE_ACTIVITY_LOGS === 'true'

// Time constants in milliseconds - replicated from store to ensure consistency
const MINUTE = 60 * 1000
const SIXTY_MINUTES = 60 * MINUTE // 3,600,000 ms (60 minutes)

function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// Communication channel between tabs using the BroadcastChannel API
const activityChannel = new BroadcastChannel('publinker_activity')

export function useActivity() {
  const activityStore = useActivityStore()
  let checkInterval = null

  const setupActivityMonitoring = () => {
    if (enableActivityLogs) {
      logger.group('🎮 Starting Activity Monitor')
      logger.log('📡 Status:', 'Setting up events')
      logger.groupEnd()
    }

    // Activity state initialization
    activityStore.resetActivity()

    const events = {
      mousedown: 'Mouse click',
      mousemove: 'Mouse movement',
      keydown: 'Keyboard input',
      scroll: 'Page scroll',
      touchstart: 'Touch screen',
      click: 'Click interaction',
      input: 'Input change'
    }

    // Function for periodic inactivity check
    const checkInactivity = () => {
      activityStore.checkInactivity()
    }

    // Activity event handler with debounce for optimization
    const handleActivity = debounce(event => {
      activityStore.updateLastActivity(
        event.type,
        `${events[event.type]} at ${event.target?.tagName?.toLowerCase() || 'unknown'}`
      )
      checkInactivity()
    }, 1000)

    // Handler for processing messages between tabs
    const handleTabMessage = event => {
      const {
        type,
        event: eventType,
        timestamp,
        details,
        sessionId
      } = event.data

      if (enableActivityLogs) {
        logger.group('📡 Tab Sync')
        logger.log('Type:', type)
        logger.log('Event:', eventType)
        logger.log('Session:', sessionId)
        logger.log('From tab:', event.target.name || 'unknown')
        logger.groupEnd()
      }

      switch (type) {
        case 'activity_update':
          activityStore.syncActivity(timestamp, eventType, details, sessionId)
          break
        case 'session_expired':
          // State configuration and automatic reload in response to session expiration
          activityStore.wasLoggedOutDueToInactivity = true
          window.location.reload()
          break
        case 'session_extended':
          activityStore.updateLastActivity(
            'cross_tab_extend',
            'Session extended from another tab'
          )
          break
        case 'tab_opened':
          // Sending current state to synchronize with the new tab
          activityChannel.postMessage({
            type: 'activity_sync',
            timestamp: activityStore.lastActivity,
            event: activityStore.lastActivityEvent,
            sessionId: activityStore.sessionId
          })
          break
        case 'activity_sync':
          // State synchronization with data from existing tab
          if (!activityStore.sessionId) {
            activityStore.syncActivity(
              timestamp,
              eventType,
              'Sync from existing tab',
              sessionId
            )
          }
          break
      }
    }

    // Handler for tab visibility state management
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (enableActivityLogs) {
          logger.group('👁️ Tab Visibility')
          logger.log('Status: Tab inactive, pausing checks')
          logger.log('Session:', activityStore.sessionId)
          logger.groupEnd()
        }

        // Suspending check interval when tab is inactive
        if (checkInterval) {
          clearInterval(checkInterval)
          checkInterval = null
        }

        // Notification to other tabs about inactive state
        activityChannel.postMessage({
          type: 'tab_inactive',
          timestamp: Date.now(),
          sessionId: activityStore.sessionId
        })
      } else {
        if (enableActivityLogs) {
          logger.group('👁️ Tab Visibility')
          logger.log('Status: Tab active, resuming checks')
          logger.log('Session:', activityStore.sessionId)
          logger.groupEnd()
        }

        // Immediate check when returning to tab
        checkInactivity()

        // Restarting check interval
        if (!checkInterval) {
          checkInterval = setInterval(checkInactivity, 5000)
        }

        // Notification to other tabs about active state
        activityChannel.postMessage({
          type: 'tab_active',
          timestamp: Date.now(),
          sessionId: activityStore.sessionId
        })
      }
    }

    // Registering event listeners for activity monitoring
    Object.keys(events).forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    // Registering listener for tab visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Registering listener for inter-tab communication
    activityChannel.addEventListener('message', handleTabMessage)

    // Initializing periodic check interval
    checkInterval = setInterval(checkInactivity, 5000)

    // Initialization notification to other tabs
    activityChannel.postMessage({
      type: 'tab_opened',
      timestamp: Date.now(),
      sessionId: activityStore.sessionId
    })

    if (enableActivityLogs) {
      logger.group('🎯 Activity Monitor')
      logger.log('✨ Status:', 'Events registered')
      logger.log(
        '🎪 Events:',
        Object.entries(events)
          .map(([event, desc]) => `${event} (${desc})`)
          .join(', ')
      )
      logger.log('👁️ Tab visibility monitoring: Active')
      logger.log('🔄 Tab sync: Active')
      logger.log('🆔 Session:', activityStore.sessionId)
      logger.groupEnd()
    }

    // Cleanup function for removing all listeners and resources
    return () => {
      if (enableActivityLogs) {
        logger.group('🧹 Cleanup Monitor')
        logger.log('🎯 Status:', 'Removing events')
        logger.log('🆔 Session:', activityStore.sessionId)
        logger.groupEnd()
      }

      // Removing event listeners
      Object.keys(events).forEach(event => {
        window.removeEventListener(event, handleActivity)
      })

      // Removing visibility listener
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      // Removing inter-tab communication listener
      activityChannel.removeEventListener('message', handleTabMessage)

      // Termination notification to other tabs
      activityChannel.postMessage({
        type: 'tab_closed',
        timestamp: Date.now(),
        sessionId: activityStore.sessionId
      })

      // Closing communication channel
      activityChannel.close()

      // Clearing check interval
      if (checkInterval) {
        clearInterval(checkInterval)
        checkInterval = null
      }
    }
  }

  // Configuration during component lifecycle
  onMounted(() => {
    // Inactivity timeout initialization with consistent value
    activityStore.inactivityTimeout = SIXTY_MINUTES

    const cleanup = setupActivityMonitoring()
    onUnmounted(() => {
      cleanup()
      if (enableActivityLogs) {
        logger.group('👋 Monitor Finished')
        logger.log('🎯 Status:', 'Component unmounted')
        logger.log('🆔 Session:', activityStore.sessionId)
        logger.groupEnd()
      }
    })
  })

  return {
    isInactive: activityStore.isInactive,
    timeRemaining: activityStore.timeRemaining,
    extendSession: () => {
      activityStore.updateLastActivity(
        'manual_extend',
        'Session manually extended'
      )
    }
  }
}
